import type { Program } from 'models';
import { type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { routes } from 'routes/routes';
import { usePermissions } from 'permissions';
import { Button, Card, Dropdown, Flex, Icon, Loading, Title } from 'ui';
import { images } from 'common/utils';
import { useProgramQuery } from '../queries';
import { useOnLeaveProgram, useProgramMenuActions } from './hooks';
import { TopBarLink } from './top-bar-link';

type P = {
  children: (data: Program) => ReactNode;
  programId: string;
};

/**
 * @deprecated This component was only ported to TypeScript as-is, not redesigned. Do not use it as a reference for new code.
 */
const ProgramLayout = ({ children, programId }: P) => {
  const { canViewProgram, canViewProgramMembers } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: program } = useProgramQuery({ programId });
  const onLeaveSuccess = useOnLeaveProgram();

  const { getMenuItems, handleMenuAction, leaveProgramContextHolder, reportProgramContextHolder } =
    useProgramMenuActions({ onLeaveSuccess });

  useEffect(() => {
    const redirectIfNeeded = async () => {
      if (!program) {
        return;
      }

      if (!canViewProgram({ program }) && location.pathname !== routes.program.about(programId)) {
        await navigate(routes.program.about(programId));
      }

      if (!canViewProgramMembers({ program }) && location.pathname === routes.program.members(programId)) {
        await navigate(routes.program.timeline(programId));
      }
    };

    void redirectIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Should run only when the program data changes
  }, [program, programId]);

  return (
    <>
      {program ? (
        <>
          <Card className="program-layout" shrinked>
            <div className="program-layout__image">
              <img alt={program.name} src={program.coverPhoto?.url ?? images.default.programCoverPhoto} />
            </div>
            <Flex align="center" className="program-layout__name" justify="center">
              <Title className="program-layout__name__text" level={1} marginBottom marginTop>
                {program.name}
              </Title>
            </Flex>
            <Flex className="program-layout__navbar">
              {canViewProgram({ program }) ? (
                <>
                  <TopBarLink title="Timeline/Newsfeed" to={routes.program.timeline(program.id)} />
                  {canViewProgramMembers({ program }) && (
                    <TopBarLink title="Members" to={routes.program.members(program.id)} />
                  )}
                  <TopBarLink title="Files" to={routes.program.assets(program.id)} />
                </>
              ) : null}
              <TopBarLink title="About" to={routes.program.about(program.id)} />
              <div className="settings-container">
                <Dropdown
                  className="settings-container"
                  menu={{
                    items: getMenuItems(program),
                    onClick({ key }) {
                      handleMenuAction(key, program);
                    },
                  }}
                  trigger={['click']}
                >
                  <Button icon={<Icon icon="more-2-line" />} type="link-secondary" />
                </Dropdown>
              </div>
            </Flex>
          </Card>
          {children(program)}
        </>
      ) : (
        <Loading />
      )}
      {leaveProgramContextHolder}
      {reportProgramContextHolder}
    </>
  );
};

export default ProgramLayout;
